// exportToXlsxUtils.js  
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // For time zone handling  
import timezone from "dayjs/plugin/timezone"; // For time zone handling  

dayjs.extend(utc);
dayjs.extend(timezone);

// Map group size to group type  
const mapSizeToType = (size) => {
    if (size === 2) return "duet";
    if (size === 3) return "trio";
    if (size >= 4 && size <= 9) return "small group";
    if (size >= 10 && size <= 19) return "large group";
    if (size >= 20) return "production line";
    return "solo";
};

// Function to adjust column widths  
const adjustColumnWidths = (data) => {
    const wscols = [];
    const columns = Object.keys(data[0]);
    columns.forEach((column) => {
        let maxLength = column.length; // Start with header length  
        data.forEach((row) => {
            const value = row[column] ? row[column].toString() : "";
            maxLength = Math.max(maxLength, value.length);
        });
        wscols.push({ wch: maxLength + 2 }); // Add extra space  
    });
    return wscols;
};

// Existing utility function to export all schedules  
const exportAllSchedulesToExcel = (scheduledApplicationsPerVenue, venues) => {
    const workbook = XLSX.utils.book_new();

    for (const venueId in scheduledApplicationsPerVenue) {
        const applications = scheduledApplicationsPerVenue[venueId];

        // Sort applications by scheduled date and start time  
        const sortedApplications = applications.slice().sort((a, b) => {
            return a.startTime - b.startTime;
        });

        const data = sortedApplications.map((application) => {
            const {
                contestant,
                group,
                event,
                scheduledDate,
                startTime,
                endTime,
            } = application;
            const name = contestant
                ? `${contestant.first_name} ${contestant.last_name}`
                : group && group.group_name
                    ? group.group_name
                    : "Unknown";
            const ageGroup = application.group_age;
            const eventName = event.name;
            const eventStyle = event.style;
            const groupSize = group
                ? mapSizeToType(group.group_size)
                : mapSizeToType(1);
            const scheduledTime = `${startTime.format("HH:mm")} - ${endTime.format(
                "HH:mm"
            )}`;
            const venueName =
                venues.find((venue) => venue._id === venueId)?.venue || "Unknown";

            return {
                "Contestant/Group Name": name,
                "Age Group": ageGroup,
                "Event Name": eventName,
                "Event Style": eventStyle,
                "Group Size": groupSize,
                "Scheduled Date": scheduledDate,
                "Scheduled Time": scheduledTime,
                Venue: venueName,
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(data);

        // Adjust column widths  
        worksheet["!cols"] = adjustColumnWidths(data);

        const venueName =
            venues.find((venue) => venue._id === venueId)?.venue ||
            `Venue_${venueId}`;
        const safeVenueName = venueName.replace(/[\\/?*[\]:]/g, ""); // Remove invalid characters  
        const truncatedVenueName = safeVenueName.substring(0, 31); // Max sheet name length is 31 characters  

        XLSX.utils.book_append_sheet(workbook, worksheet, truncatedVenueName);
    }

    XLSX.writeFile(workbook, "AllSchedules.xlsx");
};

// Updated export function to export all scheduled applications  
const exportAllApplicationsToExcel = (allApplicationsPerVenue, venues) => {
    const workbook = XLSX.utils.book_new();

    for (const venueId in allApplicationsPerVenue) {
        let applications = allApplicationsPerVenue[venueId];

        // Sort applications by scheduled date and time (application.date)  
        applications = applications.slice().sort((a, b) => {
            const dateA = a.date ? new Date(a.date) : new Date(0);
            const dateB = b.date ? new Date(b.date) : new Date(0);
            return dateA - dateB;
        });

        const data = applications.map((application) => {
            const {
                contestant,
                contestants,
                group,
                event,
                date,
            } = application;
            let name;
            let number;
            if (group && group.group_name) {
                name = group.group_name;
                number = group.group_number;
            } else if (contestant) {
                name = `${contestant.first_name} ${contestant.last_name}`;
                number = contestant.contestant_number;
            } else if (contestants && contestants.length > 0) {
                name = contestants.map((c) => `${c.first_name} ${c.last_name}`).join(", ");
            } else {
                name = "No Contestants";
            }
            const ageGroup = application.group_age;
            const eventName = event ? event.name : "No Event";
            const eventStyle = event ? event.style : "No Style";
            const groupSize = group
                ? mapSizeToType(group.group_size)
                : mapSizeToType(contestants ? contestants.length : 1);
            const scheduledDate = date
                ? dayjs.utc(date).tz("Europe/Bucharest").format("YYYY-MM-DD")
                : "Not Scheduled";
            const scheduledTime = date
                ? dayjs.utc(date).tz("Europe/Bucharest").format("HH:mm")
                : "Not Scheduled";
            const venueName =
                venueId === "noVenue"
                    ? "No Venue"
                    : venues.find((venue) => venue._id === venueId)?.venue ||
                    "Unknown Venue";

            return {
                "Number": number,
                "Contestant/Group Name": name,
                "Age Group": ageGroup || "N/A",
                "Event Name": eventName,
                "Event Style": eventStyle,
                "Group Size": groupSize,
                "Scheduled Date": scheduledDate,
                "Scheduled Time": scheduledTime,
                Venue: venueName,
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(data);

        // Adjust column widths  
        worksheet["!cols"] = adjustColumnWidths(data);

        const venueName =
            venueId === "noVenue"
                ? "No Venue"
                : venues.find((venue) => venue._id === venueId)?.venue ||
                `Venue_${venueId}`;
        const safeVenueName = venueName.replace(/[\\/?*[\]:]/g, ""); // Remove invalid characters  
        const truncatedVenueName = safeVenueName.substring(0, 31); // Max sheet name length is 31 characters  

        XLSX.utils.book_append_sheet(workbook, worksheet, truncatedVenueName);
    }

    XLSX.writeFile(workbook, "AllScheduledApplications.xlsx");
};

// --- Helper to group applications by participant (solo/group) ---  
function groupApplications(applications) {  
    const map = {};  
    applications.forEach(app => {  
      let key, name, number, type, members = [];  
      // For group_age, use application.group_age if present  
      let group_age = app.group_age || (app.group && app.group.group_age) || "";  
    
      if (app.group) {  
        key = "group_" + app.group._id;  
        name = app.group.group_name;  
        number = app.group.group_number || "";  
        type = "Group";  
        members = Array.isArray(app.group.group) ? app.group.group : [];  
    
        if (!map[key]) {  
          map[key] = {  
            type,  
            name,  
            number,  
            members,  
            applications: [],  
            teacher_name: app.group.teacher_name || "",  
            teacher_number: app.group.teacher_number || "",  
            teacher_email: app.group.teacher_email || "",  
            group_size: app.group.group_size || "",  
            group_age, // from app  
            group_type: app.group.group_type || "",  
            // extra fields, keep as blank for groups  
            gender: "",  
            id_number: "",  
            passport_number: "",  
            cell_number: "",  
            email: "",  
            ice_contact_name: "",  
            ice_contact_email: ""  
          };  
        }  
    
      } else if (app.contestant) {  
        key = "contestant_" + app.contestant._id;  
        name = `${app.contestant.first_name} ${app.contestant.last_name}`;  
        number = app.contestant.contestant_number || "";  
        type = "Contestant";  
    
        if (!map[key]) {  
          map[key] = {  
            type,  
            name,  
            number,  
            members: [],  
            applications: [],  
            teacher_name: app.ice_contact_name || "",      // will map to ICE name  
            teacher_number: app.contestant.cell_number || "",  
            teacher_email: app.ice_contact_email || "",    // will map to ICE email  
            group_size: "",  
            group_age, // from app  
            group_type: "",  
            gender: app.contestant.gender || "",  
            id_number: app.contestant.id_number || "",  
            passport_number: app.contestant.passport_number || "",  
            cell_number: app.contestant.cell_number || "",  
            email: app.contestant.email || "",  
            ice_contact_name: app.ice_contact_name || "",  
            ice_contact_email: app.ice_contact_email || ""  
          };  
        }  
      } else {  
        return;  
      }  
      map[key].applications.push(app);  
    });  
    return map;  
  }  
  
// --- Get unique event names order ---  
function getAllEventNames(applications) {  
  const names = [];  
  applications.forEach(app => {  
    if (app.event && app.event.name && !names.includes(app.event.name)) {  
      names.push(app.event.name);  
    }  
  });  
  return names;  
}  
  
// --- Utility: Build participant info ---  
function extractParticipant(entry) {  
    // All fields used for both group & contestant  
    return { ...entry };  
  }  
  
// --- Build applications map per event/style ---  
function buildApplicationsMap(applications) {  
  // Returns: { [id]: { [eventName]: { styles: [...], instruments: [...] } } }  
  const map = {};  
  applications.forEach(app => {  
    let id = app.group  
      ? "group_" + app.group._id  
      : app.contestant  
        ? "contestant_" + app.contestant._id  
        : null;  
    if (!id) return;  
    if (!map[id]) map[id] = {};  
    const event = app.event ? app.event.name : "";  
    if (!event) return;  
  
    if (!map[id][event]) map[id][event] = { styles: [], instruments: [] };  
  
    // Add style  
    if (app.event && app.event.style) {  
      map[id][event].styles.push(app.event.style);  
    }  
    // Add instrument, if present/possible  
    if (app.instrument) {  
      map[id][event].instruments.push(app.instrument);  
    }  
  });  
  
  // Deduplicate styles/instruments  
  for (let id in map) {  
    for (let event in map[id]) {  
      map[id][event].styles = Array.from(new Set(map[id][event].styles));  
      map[id][event].instruments = Array.from(  
        new Set(map[id][event].instruments.filter(Boolean))  
      );  
    }  
  }  
  return map;  
}  
  
function exportPaidApplications(applications) {
  // Filter: paid and round = 2
  const paidApps = applications.filter(app => app.paid && app.round === 2);
  if (!paidApps.length) {
    window.alert("No paid round 2 applications to export.");
    return;
  }

  const grouped = groupApplications(paidApps);
  const ids = Object.keys(grouped);

  // Sort ids by audition date
  ids.sort((a, b) => {
    const appA = grouped[a].applications[0];
    const appB = grouped[b].applications[0];
    const dateA = appA && appA.province && appA.province.date ? new Date(appA.province.date) : new Date(0);
    const dateB = appB && appB.province && appB.province.date ? new Date(appB.province.date) : new Date(0);
    return dateA - dateB;
  });

  const eventNames = getAllEventNames(paidApps);

  // Base columns (without Amount Paid for now)
  const baseColumns = [
    "Audition Date",
    "Type",
    "Name",
    "Number",
    "Group Members",
    "Teacher Name",
    "Teacher Number",
    "Teacher Email",
    "Group Size",
    "Group Age",
    "Group Type",
    "Gender",
    "ID Number",
    "Passport Number",
    "Cell Number",
    "Email"
  ];

  // Event columns (+instrument if needed)
  const eventColumnSpecs = [];
  eventNames.forEach(eventName => {
    eventColumnSpecs.push({ name: eventName });
    if (eventName.toLowerCase().includes("instrumental")) {
      eventColumnSpecs.push({ name: `${eventName} - Instrument` });
    }
  });

  // Make sure Amount Paid is the final column
  const allColumns = [
    ...baseColumns,
    ...eventColumnSpecs.map(e => e.name),
    "Amount Paid"
  ];

  const appsMap = buildApplicationsMap(paidApps);

  const data = ids.map(id => {
    const entry = grouped[id];
    const {
      type,
      name,
      number,
      members,
      teacher_name,
      teacher_number,
      teacher_email,
      group_size,
      group_age,
      group_type,
      gender,
      id_number,
      passport_number,
      cell_number,
      email
    } = extractParticipant(entry);

    // Audition info
    let auditionVal = "";
    const firstApp = entry.applications[0];
    if (
      firstApp &&
      firstApp.province &&
      firstApp.province.province &&
      firstApp.province.location &&
      firstApp.province.date
    ) {
      const dateStr = firstApp.province.date;
      const dateObj = new Date(dateStr);
      const sastDateObj = new Date(dateObj.getTime() + (2 * 60 * 60 * 1000)); // UTC+2
      const yyyy = sastDateObj.getUTCFullYear();
      const mm = String(sastDateObj.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(sastDateObj.getUTCDate()).padStart(2, "0");
      const sastDateOnly = `${yyyy}-${mm}-${dd}`;
      auditionVal = `${firstApp.province.province} - ${firstApp.province.location} - ${sastDateOnly}`;
    }

    // NEW: include each group member's name, id, and number
    let groupMembersText = "";
    if (type === "Group" && Array.isArray(members) && members.length > 0) {
      groupMembersText = members.map(m => {
        const memberName = m?.name || "";
        const memberId = m?.id_number || "";
        const memberNum = (m && (m.contestant_number ?? m.number)) ?? "";
        return `${memberName} [ID: ${memberId}] [Number: ${memberNum}]`;
      }).join("; ");
    }

    const row = {
      "Audition Date": auditionVal,
      Type: type,
      Name: name,
      Number: number,
      "Group Members": groupMembersText,
      "Teacher Name": teacher_name,
      "Teacher Number": teacher_number,
      "Teacher Email": teacher_email,
      "Group Size": group_size,
      "Group Age": group_age,
      "Group Type": group_type,
      Gender: gender,
      "ID Number": id_number,
      "Passport Number": passport_number,
      "Cell Number": cell_number,
      Email: email
    };

    const events = appsMap[id] || {};
    eventColumnSpecs.forEach(spec => {
      if (spec.name.endsWith(" - Instrument")) {
        const evName = spec.name.replace(" - Instrument", "");
        row[spec.name] =
          events[evName] && events[evName].instruments.length
            ? events[evName].instruments.join(", ")
            : "";
      } else {
        row[spec.name] =
          events[spec.name] && events[spec.name].styles.length
            ? events[spec.name].styles.join(", ")
            : "";
      }
    });

    // ----- Amount Paid (Round 2 formula) -----
    // Base fee 2050 covers up to 6 applications, then 280 per extra application
    const totalAppsForEntry = entry.applications.length;
    let amountPaid = 0;

    if (totalAppsForEntry > 0) {
      if (totalAppsForEntry <= 6) {
        amountPaid = 2050;
      } else {
        amountPaid = 2050 + (totalAppsForEntry - 6) * 280;
      }
    }

    row["Amount Paid"] = amountPaid;

    return row;
  });

  // Match column order
  const finalRows = data.map(row =>
    Object.fromEntries(allColumns.map(col => [col, row[col] || ""]))
  );

  const ws = XLSX.utils.json_to_sheet(finalRows);
  ws["!cols"] = allColumns.map(() => ({ wch: 20 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Paid Round 2 Applications");
  XLSX.writeFile(wb, "paid_round2_applications.xlsx");
}

export {
    exportAllSchedulesToExcel,
    exportAllApplicationsToExcel,
    exportPaidApplications
};  